import { Box, Skeleton, styled } from "@mui/material";
import { useState } from "react";
import { formatPhone, validateEmail } from "@/utils/StringUtils";
import { useCheckoutUI } from "../../../CheckoutUIProvider";

import { isShopifyProduct, TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";

import { updateEmail } from "@/api/rest/authenticated/Address";
import { SolidButton } from "@/components/StoreComponents/StoreButton";

const EmailSection = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  marginTop: "32px",
}));

const SectionTitle = styled("div")(({ theme }) => ({
  fontSize: "20px",
  fontWeight: 600,
  color: theme.vars.palette.text.primary,

  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: "16px",
  },
}));

const SectionSubtitle = styled("div")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
  },
}));

const InputContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    flexDirection: "column",
    gap: "8px",
  },
}));

const StyledInputWrapper = styled("div")(({ theme }) => ({
  flex: 1,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    width: "100%",
  },
}));

const AddEmail = () => {
  const [email, setEmail] = useState("");
  const [machineState, send] = useCheckoutUI();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const { status, checkoutIntent } = await updateEmail({
        checkoutIntentId: machineState.context.checkoutIntent
          ?.checkoutIntentId as string,
        email,
      });

      if (status === "success") {
        send({
          type: "SET_CHECKOUT_INTENT",
          data: { checkoutIntent: checkoutIntent },
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  if (isSaving) {
    return (
      <div>
        <Skeleton variant="text" width={100} height={20} />
      </div>
    );
  }

  return (
    <EmailSection>
      <SectionTitle>Email address</SectionTitle>
      <SectionSubtitle>
        Add your email for priority customer support and seamless tracking
      </SectionSubtitle>
      <InputContainer>
        <StyledInputWrapper>
          <CheckoutInput
            label="Email"
            name="email"
            autoComplete="email"
            value={email}
            size="medium"
            style={{
              minWidth: "300px",
              maxWidth: "100%",
              backgroundColor: "#fff",
            }}
            onChange={(e) => {
              const value = e.target.value;
              setEmail(value?.trim());
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
            InputLabelProps={{
              sx: {
                top: "5px",
                "&.MuiInputLabel-shrink": {
                  top: "2px",
                },
              },
            }}
            slotProps={{
              input: {
                sx: (theme) => ({
                  paddingRight: "16px",
                  height: "61px",
                  ".MuiInputBase-input": {
                    "&:-webkit-autofill": {
                      WebkitBoxShadow: "0 0 0 100px #ffffff inset",
                      WebkitTextFillColor: theme.palette.primary.main,
                      fontSize: "14px",
                    },
                  },
                  [theme.breakpoints.down("md")]: {
                    height: "61px",
                  },
                }),

                disableUnderline: true,
                endAdornment: (
                  <SolidButton
                    sx={(theme) => ({
                      "&:disabled": {
                        backgroundColor: "var(--dtc-palette-action-disabled)",
                        color: "#fff",
                      },
                    })}
                    disabled={isSaving || !validateEmail(email)}
                    height={{
                      sm: "28px",
                      md: "38px",
                      lg: "38px",
                      xl: "38px",
                    }}
                    onClick={handleSave}
                  >
                    Save
                  </SolidButton>
                ),
              },
            }}
            sx={{
              flex: 1,
            }}
          />
        </StyledInputWrapper>
      </InputContainer>
    </EmailSection>
  );
};

const CheckoutAccountInfo = () => {
  const [state] = useCheckoutUI();

  const accountInformation = state.context.checkoutIntent?.accountInformation;

  const email = accountInformation?.email;

  if (!state.context.checkoutIntent?.retailerId) {
    return null;
  }

  return (
    <Box
      sx={{
        fontSize: 16,
        [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
          fontSize: 14,
        },
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {state.context.checkoutIntent?.accountInformation?.firstName && (
          <div style={{}}>
            {state.context.checkoutIntent?.accountInformation?.firstName}{" "}
            {state.context.checkoutIntent?.accountInformation?.lastName}
          </div>
        )}
        <div>
          {formatPhone(
            state.context.checkoutIntent?.accountInformation?.phoneNumber || ""
          )}
        </div>
        {isShopifyProduct(state.context.checkoutIntent?.retailerId) ? (
          email ? (
            <div>{email}</div>
          ) : (
            <AddEmail />
          )
        ) : null}
      </div>
    </Box>
  );
};

export default CheckoutAccountInfo;
